import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbTag } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { type LabelListPayload, parseLabelListPayload } from "./chiefSchema";

interface LabelListRendererProps {
  block: StreamingBlock;
}

export const LabelListRenderer = (props: LabelListRendererProps) => {
  const { t } = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<LabelListPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseLabelListPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {t("chief_labels_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().labels.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbTag class="size-4 shrink-0 text-brand" />
                <span class="text-sm font-medium text-primary-text">{t("chief_labels_card.title")}</span>
                <span class="ml-auto text-xs text-muted-text">
                  {data().labels.length === 1
                    ? t("chief_labels_card.results_one")
                    : t("chief_labels_card.results", { count: String(data().labels.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().labels}>
                  {(label) => (
                    <li class="px-3 py-2">
                      <div class="flex min-w-0 items-center gap-2">
                        <Show
                          when={label.color}
                          fallback={
                            <span class="size-2.5 shrink-0 rounded-full bg-primary-border" aria-hidden="true" />
                          }
                        >
                          <span
                            class="size-2.5 shrink-0 rounded-full"
                            style={{ "background-color": label.color }}
                            aria-hidden="true"
                          />
                        </Show>
                        <span class="truncate text-sm text-primary-text">{label.name}</span>
                      </div>
                      <Show when={label.description}>
                        <p class="mt-0.5 truncate ps-4.5 text-xs text-muted-text">{label.description}</p>
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
